import {Filter} from '@github-ui/filter'
import {Link, useParams} from '@github-ui/react-router'
import {type EditableShortcutField, ShortcutEditingHeader, type UserShortcut} from '@github-ui/saved-views'
import {useFilterProviders} from '@github-ui/shared-filter-providers/providers/use-filter-providers'
import {useDebounce} from '@github-ui/use-debounce'
import {useNavigate} from '@github-ui/use-navigate'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {Heading} from '@primer/react'
import {useCallback, useEffect, useState} from 'react'

import {useCreateSavedViewMutation} from '../../hooks/use-create-saved-view-mutation'
import styles from './RepositoryViewNewPage.module.css'
import {RepositoryViewNewPreview} from './RepositoryViewNewPreview'

function buildInitialShortcut(owner: string, repo: string): UserShortcut {
  const query = owner && repo ? `is:issue repo:${owner}/${repo}` : 'is:issue'
  return {
    id: '',
    name: '',
    searchType: 'issues',
    query,
    description: '',
    icon: 'bookmark',
    color: 'gray',
  }
}

export function RepositoryViewNewContent() {
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  const navigate = useNavigate()

  const [shortcut, setShortcut] = useState<UserShortcut>(() => buildInitialShortcut(owner, repo))

  const setShortcutValue = useCallback((field: EditableShortcutField, value: string) => {
    setShortcut(prev => ({...prev, [field]: value}))
  }, [])

  const [debouncedPreviewQuery, setDebouncedPreviewQuery] = useState(shortcut.query)
  const debouncedSetPreviewQuery = useDebounce(setDebouncedPreviewQuery, 300)
  useEffect(() => {
    debouncedSetPreviewQuery(shortcut.query)
  }, [shortcut.query, debouncedSetPreviewQuery])

  const handlePreviewQueryChange = useCallback(
    (value: string) => {
      setShortcutValue('query', value)
      setDebouncedPreviewQuery(value)
    },
    [setShortcutValue],
  )

  const filterProviders = useFilterProviders({
    repositoryScope: owner && repo ? `${owner}/${repo}` : undefined,
    isIssueFieldsEnabled: true,
  })

  const {mutate, isPending, error} = useCreateSavedViewMutation({owner, repo})

  const viewsPath = `/${owner}/${repo}/issues/views`

  const handleDismiss = useCallback(() => navigate(viewsPath), [navigate, viewsPath])

  const handleSave = useCallback(() => {
    mutate(
      {
        name: shortcut.name.trim(),
        query: shortcut.query.trim(),
        description: shortcut.description?.trim() || undefined,
        icon: shortcut.icon,
        color: shortcut.color,
      },
      {
        onSuccess: ({savedView}) => {
          navigate(`/${owner}/${repo}/issues/views/${savedView.id}`)
        },
      },
    )
  }, [mutate, navigate, owner, repo, shortcut])

  return (
    <div className={styles.middlePaneWrapper} data-hpc>
      <Link to={viewsPath} className={styles.backLink}>
        <ArrowLeftIcon />
        Back to Views
      </Link>
      <Heading as="h1" variant="medium">
        Create view
      </Heading>
      <div className={styles.middlePaneGrid}>
        <ShortcutEditingHeader
          shortcut={shortcut}
          setShortcutValue={setShortcutValue}
          onSave={handleSave}
          onDismiss={handleDismiss}
          isSaving={isPending}
          errorMessage={error?.message}
          renderFilter={({inputRef, className}) => (
            <Filter
              id="repo-view-new-filter"
              className={className}
              inputRef={inputRef}
              label="Query"
              placeholder="Search issues"
              variant="input"
              providers={filterProviders}
              filterValue={shortcut.query}
              onChange={value => setShortcutValue('query', value)}
              settings={{aliasMatching: true, groupAndKeywordSupport: true}}
            />
          )}
        />
        <RepositoryViewNewPreview
          owner={owner}
          repo={repo}
          query={debouncedPreviewQuery}
          onQueryChange={handlePreviewQueryChange}
        />
      </div>
    </div>
  )
}
